import { zodResolver } from '@hookform/resolvers/zod';
import { pageTitle } from '@palaccount/config';
import {
  ITEM_AVAILABILITY,
  ITEM_MESSAGES,
  SEE_ITEM_COST,
  type CreateItemRequest,
  type ItemResponse,
} from '@palaccount/contracts';
import { Copy, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Amount, Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentHeaderControls } from '@/components/document-header-controls';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyField } from '@/components/ui/currency-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableCell } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { currencyNotOffered, useCurrencyPickerCodes } from '@/features/fx/api';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { QuickCreateSelect } from '@/features/quick-create/quick-create-select';
import { localizedName } from '@/lib/localized-name';
import { useCan } from '@/lib/permissions';
import { localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useCreateItem,
  useItem,
  useItemActive,
  useItemCategories,
  useItemDelete,
  useManufacturers,
  usePriceTiers,
  useUnits,
  useUpdateItem,
} from './api';
import { isBelowCostQuestion } from './below-cost';
import { isValidFactor, multiplyQtyByFactor, trimZeros } from './conversion';
import { ItemCell } from './item-cell';
import { useItemPicker } from './item-picker';
import { isBelowReorder } from './stock-format';
import {
  buildTierRows,
  isValidTierPrice,
  pricesFromRows,
  setTierPrice,
  type TierPriceRow,
} from './tier-rows';

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const COST_RE = /^\d{1,11}(\.\d{1,4})?$/;
const QTY0_RE = /^\d{1,12}(\.\d{1,3})?$/;
const INT_RE = /^\d+$/;

/**
 * MD-03 §5 — the item editor's head fields. The form speaks strings; the
 * contract request is built on submit with empties pruned. Messages are i18n
 * keys, translated at render (the partner-form pattern). The four child
 * collections (units, tier prices, variants, kit components) live beside the
 * form as row state, validated by the same pure helpers the tests exercise.
 */
export const ItemFormSchema = z.object({
  codeAuto: z.boolean(),
  code: z.string().trim().max(40),
  name: z.string().trim().min(1, 'items.errName').max(200, 'items.errName'),
  /** S5 / IT-C1: the Arabic twin and the printed name; blank = not set. */
  nameAr: z.string().trim().max(200),
  printName: z.string().trim().max(200),
  description: z.string().trim().max(1000),
  /** IT-S9. */
  specifications: z.string().trim().max(4000),
  /** IT-S5. */
  availability: z.enum(ITEM_AVAILABILITY),
  /** IT-S7. */
  posVisible: z.boolean(),
  /** IT-S3: '' = the base currency. Free text, like the warehouse's box. */
  priceCurrency: z.string().trim().max(3),
  type: z.enum(['product', 'service', 'bundle']),
  categoryId: z.string().min(1, 'items.errName'),
  stockUnitId: z.string().min(1),
  defaultBuyUnitId: z.string(),
  defaultSellUnitId: z.string(),
  unitPrice: z.union([z.literal(''), z.string().regex(MONEY_RE, 'items.errMoney')]),
  costPrice: z.union([z.literal(''), z.string().regex(COST_RE, 'items.errCost')]),
  hideCost: z.boolean(),
  taxCategory: z.string().trim().max(60),
  inventoryAccountCode: z.string().trim().max(20),
  multiTier: z.boolean(),
  autoDeduct: z.boolean(),
  trackingEnabled: z.boolean(),
  trackingType: z.enum(['serial', 'batch']),
  batchNo: z.string().trim().max(40),
  expiryDate: z.string(),
  reorderPoint: z.union([z.literal(''), z.string().regex(QTY0_RE, 'items.errNegativeReorder')]),
  reorderQty: z.union([z.literal(''), z.string().regex(QTY0_RE, 'items.errNegativeReorder')]),
  /** PART 8 §8.1 — the line that turns a stock row blue; blank = no ceiling. */
  overstockCeiling: z.union([z.literal(''), z.string().regex(QTY0_RE, 'items.errNegativeReorder')]),
  /** bugs: Items — OFF keeps this item out of the Low/Out counts and badges. */
  trackStockAlerts: z.boolean(),
  preferredSupplierId: z.string(),
  supplierItemCode: z.string().trim().max(40),
  leadTimeDays: z.union([z.literal(''), z.string().regex(INT_RE, 'items.errDays')]),
  /** IT-S6: '' = not stated. */
  manufacturerId: z.string(),
});

export type ItemFormValues = z.infer<typeof ItemFormSchema>;

interface UnitRowState {
  unitId: string;
  factor: string;
  allowBuy: boolean;
  allowSell: boolean;
}

interface VariantRowState {
  sku: string;
  attributes: string;
  priceOverride: string;
}

interface ComponentRowState {
  componentItemId: string;
  qty: string;
  unitId: string;
}

/**
 * One row of the Barcodes grid (bugs: New Item — IT-N9 "Unit / Barcode / Size
 * / Notes"). Row state rather than form fields, like the other child
 * collections: the list is saved whole. `unitId` '' = no unit stated. The
 * 4..60 bound is the contract's own (`ITEM_MESSAGES.barcodeLength`) and the
 * database's CHECK; `validateChildren` repeats it so a person hears it while
 * the cursor is still in the box rather than after a round trip.
 */
export interface BarcodeRowState {
  unitId: string;
  barcode: string;
  size: string;
  notes: string;
}

const BARCODE_MIN = 4;
const BARCODE_MAX = 60;

/**
 * One warehouse's row of the Stock-by-warehouse table's editable half (bugs:
 * New Item — IT-S1 "reorder point / qty / overstock ceiling PER WAREHOUSE").
 * Blank = the item's own figure applies at that location.
 */
export interface WarehouseLevelRowState {
  warehouseId: string;
  reorderPoint: string;
  reorderQty: string;
  overstockCeiling: string;
}

const EMPTY: ItemFormValues = {
  codeAuto: true,
  code: '',
  name: '',
  nameAr: '',
  printName: '',
  description: '',
  specifications: '',
  availability: 'available',
  posVisible: true,
  priceCurrency: '',
  type: 'product',
  categoryId: '',
  stockUnitId: '',
  defaultBuyUnitId: '',
  defaultSellUnitId: '',
  unitPrice: '',
  costPrice: '',
  hideCost: true,
  taxCategory: '',
  inventoryAccountCode: '',
  multiTier: false,
  autoDeduct: true,
  trackingEnabled: false,
  trackingType: 'serial',
  batchNo: '',
  expiryDate: '',
  reorderPoint: '',
  reorderQty: '',
  overstockCeiling: '',
  trackStockAlerts: true,
  preferredSupplierId: '',
  supplierItemCode: '',
  leadTimeDays: '',
  manufacturerId: '',
};

function fromItem(item: ItemResponse): ItemFormValues {
  return {
    codeAuto: item.itemNo !== null,
    code: item.code,
    name: item.name,
    nameAr: item.nameAr ?? '',
    printName: item.printName ?? '',
    description: item.description ?? '',
    specifications: item.specifications ?? '',
    availability: item.availability,
    posVisible: item.posVisible,
    priceCurrency: item.priceCurrency ?? '',
    type: item.type,
    categoryId: item.categoryId,
    stockUnitId: item.stockUnitId,
    defaultBuyUnitId: item.defaultBuyUnitId ?? '',
    defaultSellUnitId: item.defaultSellUnitId ?? '',
    unitPrice: item.unitPrice,
    costPrice: item.costPrice ?? '',
    hideCost: item.hideCost,
    taxCategory: item.taxCategory ?? '',
    inventoryAccountCode: item.inventoryAccountCode ?? '',
    multiTier: item.multiTier,
    autoDeduct: item.autoDeduct,
    trackingEnabled: item.trackingType !== 'none',
    trackingType: item.trackingType === 'batch' ? 'batch' : 'serial',
    batchNo: item.batchNo ?? '',
    expiryDate: item.expiryDate ?? '',
    // TRIMMED ON PURPOSE, and not an N-4 site. These three are the values of
    // EDITABLE inputs, not printed quantities: N-4 governs how a quantity is
    // SHOWN, and pre-filling a text box with "0.000" makes a person delete
    // three characters before they can type. What the field is worth when it
    // is drawn as a figure obeys the rule; what it holds while it is being
    // typed does not.
    reorderPoint: trimZeros(item.reorderPoint),
    reorderQty: trimZeros(item.reorderQty),
    overstockCeiling: item.overstockCeiling === null ? '' : trimZeros(item.overstockCeiling),
    trackStockAlerts: item.trackStockAlerts,
    preferredSupplierId: item.preferredSupplierId ?? '',
    supplierItemCode: item.supplierItemCode ?? '',
    leadTimeDays: item.leadTimeDays === null ? '' : String(item.leadTimeDays),
    manufacturerId: item.manufacturerId ?? '',
  };
}

/** Builds the contract body from head fields + row state, empties pruned. */
export function toItemRequest(
  values: ItemFormValues,
  unitRows: UnitRowState[],
  tierRows: TierPriceRow[],
  variantRows: VariantRowState[],
  componentRows: ComponentRowState[],
  /** MD-03 §9: false = the cost box was never drawn, so never send one. */
  canSeeCost = true,
  barcodeRows: BarcodeRowState[] = [],
  warehouseLevelRows: WarehouseLevelRowState[] = [],
): CreateItemRequest {
  const text = (value: string) => (value.trim() === '' ? undefined : value.trim());
  const isService = values.type === 'service';
  return {
    ...(values.codeAuto ? {} : { code: values.code.trim() }),
    name: values.name.trim(),
    nameAr: text(values.nameAr),
    printName: text(values.printName),
    description: text(values.description),
    specifications: text(values.specifications),
    availability: values.availability,
    posVisible: values.posVisible,
    priceCurrency: text(values.priceCurrency)?.toUpperCase(),
    type: values.type,
    categoryId: values.categoryId,
    stockUnitId: values.stockUnitId,
    defaultBuyUnitId: text(values.defaultBuyUnitId),
    defaultSellUnitId: text(values.defaultSellUnitId),
    unitPrice: text(values.unitPrice),
    // Never send a cost we were never shown — an empty box would blank it,
    // and the API refuses the write anyway (ITEM_MESSAGES.costDenied).
    costPrice: isService || !canSeeCost ? undefined : text(values.costPrice),
    hideCost: values.hideCost,
    taxCategory: text(values.taxCategory),
    inventoryAccountCode: text(values.inventoryAccountCode),
    multiTier: values.multiTier,
    autoDeduct: values.autoDeduct,
    trackingType: isService || !values.trackingEnabled ? 'none' : values.trackingType,
    batchNo:
      !isService && values.trackingEnabled && values.trackingType === 'batch'
        ? text(values.batchNo)
        : undefined,
    expiryDate:
      !isService && values.trackingEnabled && values.trackingType === 'batch'
        ? text(values.expiryDate)
        : undefined,
    reorderPoint: text(values.reorderPoint),
    reorderQty: text(values.reorderQty),
    overstockCeiling: isService ? undefined : text(values.overstockCeiling),
    trackStockAlerts: values.trackStockAlerts,
    preferredSupplierId: text(values.preferredSupplierId),
    supplierItemCode: text(values.supplierItemCode),
    leadTimeDays: values.leadTimeDays === '' ? undefined : Number(values.leadTimeDays),
    manufacturerId: text(values.manufacturerId),
    units: unitRows
      .filter((row) => row.unitId !== '')
      .map((row) => ({
        unitId: row.unitId,
        factor: row.factor.trim(),
        allowBuy: row.allowBuy,
        allowSell: row.allowSell,
      })),
    prices: values.multiTier ? pricesFromRows(tierRows) : pricesFromRows(tierRows),
    variants: variantRows
      .filter((row) => row.sku.trim() !== '' || row.attributes.trim() !== '')
      .map((row) => ({
        sku: row.sku.trim(),
        attributes: row.attributes.trim(),
        ...(row.priceOverride.trim() === '' ? {} : { priceOverride: row.priceOverride.trim() }),
      })),
    components:
      values.type === 'bundle'
        ? componentRows
            .filter((row) => row.componentItemId !== '')
            .map((row) => ({
              componentItemId: row.componentItemId,
              qty: row.qty.trim(),
              ...(row.unitId === '' ? {} : { unitId: row.unitId }),
            }))
        : [],
    /*
     * IT-N9: the grid, whole (replace-set), first row = the primary the till
     * matches. An untouched row is pruned rather than sent blank, because the
     * contract's minimum is 4 and "not scanned" is most of a catalogue. The
     * legacy single `barcode` field is deliberately NOT sent: on an update an
     * empty list here is what takes the last label off (the server derives
     * the primary from the list), so there is no separate null to send.
     */
    barcodes: barcodeRows
      .filter((row) => row.barcode.trim() !== '')
      .map((row) => ({
        barcode: row.barcode.trim(),
        unitId: row.unitId === '' ? null : row.unitId,
        ...(row.size.trim() === '' ? {} : { size: row.size.trim() }),
        ...(row.notes.trim() === '' ? {} : { notes: row.notes.trim() }),
      })),
    /*
     * IT-S1: the per-warehouse figures, whole (replace-set). A row with every
     * box blank is pruned — it says "the item's figures apply", which is what
     * no row says. Blank = null on the wire, never '' (the contract's regex).
     */
    warehouseLevels: isService
      ? []
      : warehouseLevelRows
          .filter(
            (row) =>
              row.reorderPoint.trim() !== '' ||
              row.reorderQty.trim() !== '' ||
              row.overstockCeiling.trim() !== '',
          )
          .map((row) => ({
            warehouseId: row.warehouseId,
            reorderPoint: text(row.reorderPoint) ?? null,
            reorderQty: text(row.reorderQty) ?? null,
            overstockCeiling: text(row.overstockCeiling) ?? null,
          })),
  };
}

function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-sm text-destructive">{t(error)}</p> : null}
    </div>
  );
}

/**
 * MD-03 §4b/§5 — the item editor: the nine panels as stacked cards on one
 * scrolling page (no tabs, §8). Bundle reveals the kit panel; Service hides
 * stock and tracking; Save is greyed until Name, Category and Stock unit
 * have values and never waits on the code (§6).
 */
export function ItemFormPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const isEdit = Boolean(itemId);
  // MD-03 §9: "Without it the field is not rendered at all, and the switch is
  // pointless." The API does not send the number either — this only decides
  // what to draw around a value that is already absent.
  const canSeeCost = useCan(SEE_ITEM_COST);

  const { data: existing, isPending: loadingExisting } = useItem(itemId);
  /**
   * "New similar" (bugs: New Item — "copies the current item with everything
   * in it, but a different item number"). `?copy=<id>` on the NEW form loads
   * that item as the starting point; the code goes back to Auto and the
   * barcode and variant SKUs are cleared, because those must be unique.
   */
  const [searchParams] = useSearchParams();
  const copyId = isEdit ? undefined : (searchParams.get('copy') ?? undefined);
  const { data: copySource } = useItem(copyId);
  const { data: units } = useUnits();
  const { data: tiers } = usePriceTiers(true);
  const { data: categories } = useItemCategories();
  // IT-S6: switched-off makers ride along so the one already on the record
  // still displays; the select offers only the active ones (plus that one).
  const { data: manufacturers } = useManufacturers(true);

  const createItem = useCreateItem();
  const updateItem = useUpdateItem(itemId ?? '');
  const itemActive = useItemActive(itemId ?? '');
  /** IT-N4: the soft delete and its undo. */
  const itemDelete = useItemDelete(itemId ?? '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [serverError, setServerError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [childError, setChildError] = useState<string | null>(null);
  const [costRevealed, setCostRevealed] = useState(false);
  /**
   * BR-MD-05-08's question, once the server has asked it. Holding the SERVER's
   * sentence rather than composing one here is what keeps ERR-MD-05-03 a
   * single wording: the client that cannot see the cost gets the figure-free
   * variant automatically, because the server chose which to send.
   */
  const [belowCostAsk, setBelowCostAsk] = useState<string | null>(null);
  /** MD-05 §2's Manager — "Approves a list that cuts prices below cost". */
  const canApproveBelowCost = useCan('inventory.approve');

  const [unitRows, setUnitRows] = useState<UnitRowState[]>([]);
  const [tierRows, setTierRows] = useState<TierPriceRow[]>([]);
  const [variantRows, setVariantRows] = useState<VariantRowState[]>([]);
  const [componentRows, setComponentRows] = useState<ComponentRowState[]>([]);
  const [barcodeRows, setBarcodeRows] = useState<BarcodeRowState[]>([]);
  const [warehouseLevelRows, setWarehouseLevelRows] = useState<WarehouseLevelRowState[]>([]);
  /** GEN-50 — field 30's supplier picker: searched, never the whole list. */
  const supplierPicker = usePartnerPicker(null, { type: 'supplier' });
  const [hasVariants, setHasVariants] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t(isEdit ? 'items.editTitle' : 'items.newTitle'));
  }, [t, isEdit]);

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(ItemFormSchema),
    defaultValues: EMPTY,
    mode: 'onSubmit',
  });

  useEffect(() => {
    const source = existing ?? copySource;
    if (source) {
      form.reset(existing ? fromItem(source) : { ...fromItem(source), codeAuto: true, code: '' });
      // IT-S1: one editable row per warehouse the item has stock in, with the
      // stored override laid over it (blank = the item's figure). A copy
      // ("New similar") carries them: they are figures, not identifiers.
      setWarehouseLevelRows(
        source.stock.map((row) => {
          const level = source.warehouseLevels.find((wl) => wl.warehouseId === row.warehouseId);
          return {
            warehouseId: row.warehouseId,
            reorderPoint: level?.reorderPoint ?? '',
            reorderQty: level?.reorderQty ?? '',
            overstockCeiling: level?.overstockCeiling ?? '',
          };
        }),
      );
      // "New similar" clears the labels too: a barcode is unique per tenant.
      setBarcodeRows(
        existing
          ? source.barcodes.map((row) => ({
              unitId: row.unitId ?? '',
              barcode: row.barcode,
              size: row.size ?? '',
              notes: row.notes ?? '',
            }))
          : [],
      );
      setUnitRows(
        source.units.map((row) => ({
          unitId: row.unitId,
          factor: row.factor,
          allowBuy: row.allowBuy,
          allowSell: row.allowSell,
        })),
      );
      setVariantRows(
        source.variants.map((row) => ({
          sku: existing ? row.sku : '',
          attributes: row.attributes,
          priceOverride: row.priceOverride ?? '',
        })),
      );
      setComponentRows(
        source.components.map((row) => ({
          componentItemId: row.componentItemId,
          qty: trimZeros(row.qty),
          unitId: row.unitId,
        })),
      );
      setHasVariants(source.variants.length > 0);
    }
  }, [existing, copySource, form]);

  // BR-MD-03-21: one row per tier the tenant has defined — built from the
  // tier LIST, with the item's stored prices laid over it.
  useEffect(() => {
    if (tiers) setTierRows(buildTierRows(tiers, (existing ?? copySource)?.prices ?? []));
  }, [tiers, existing, copySource]);

  const errors = form.formState.errors;
  const submitting = form.formState.isSubmitting;
  const name = form.watch('name');
  const categoryId = form.watch('categoryId');
  const stockUnitId = form.watch('stockUnitId');
  const codeAuto = form.watch('codeAuto');
  const type = form.watch('type');
  const hideCost = form.watch('hideCost');
  const multiTier = form.watch('multiTier');
  const trackingEnabled = form.watch('trackingEnabled');
  const trackingType = form.watch('trackingType');
  const priceCurrency = form.watch('priceCurrency');
  /** IT-S3: the tenant's offerable codes, the held one appended (fx/api). */
  const currencyCodes = useCurrencyPickerCodes(priceCurrency);

  const isService = type === 'service';
  const isBundle = type === 'bundle';

  const stockUnit = useMemo(
    () => (units ?? []).find((unit) => unit.id === stockUnitId),
    [units, stockUnitId],
  );

  /** MD-04 §5 field 3: the category default fills the stock unit on a NEW item. */
  const onCategoryChange = (nextCategoryId: string) => {
    form.setValue('categoryId', nextCategoryId);
    if (!isEdit && !stockUnitId) {
      const category = (categories ?? []).find((row) => row.id === nextCategoryId);
      if (category?.defaultUnitId) form.setValue('stockUnitId', category.defaultUnitId);
      if (category?.defaultTax && !form.getValues('taxCategory')) {
        form.setValue('taxCategory', category.defaultTax);
      }
    }
  };

  /*
   * GEN-50 — the items a kit may reference: Product or Service, never a kit
   * (BR-MD-03-05b), never itself, never a switched-off one. All four are
   * SERVER predicates now (`kitComponent`, `excludeId`, `status`), because a
   * page narrowed afterwards in the browser is a panel that hides matches and
   * says nothing about it.
   */
  const componentPicker = useItemPicker(
    useMemo(() => componentRows.map((row) => row.componentItemId), [componentRows]),
    { status: 'active', kitComponent: true, ...(itemId ? { excludeId: itemId } : {}) },
  );
  const componentById = componentPicker.byId;

  /** Field 8e: components total, beside the kit's own price (exact, 2dp). */
  const componentsTotal = useMemo(() => {
    let cents = 0n;
    for (const row of componentRows) {
      const component = componentById.get(row.componentItemId);
      // No cost, no components total — the figure is the sum of costs, and
      // it is not shown at all without the grant (MD-03 §9).
      if (!component?.costPrice || !/^\d+(\.\d+)?$/.test(row.qty)) continue;
      const line = multiplyQtyByFactor(row.qty, component.costPrice, 2);
      const [i = '0', f = ''] = line.split('.');
      cents += BigInt(i) * 100n + BigInt((f + '00').slice(0, 2));
    }
    const whole = cents / 100n;
    const frac = (cents % 100n).toString().padStart(2, '0');
    return `${whole}.${frac}`;
  }, [componentRows, componentById]);

  const validateChildren = (): string | null => {
    for (const row of unitRows) {
      if (row.unitId === '') continue;
      if (!isValidFactor(row.factor)) return t('items.errFactorZero');
    }
    for (const row of barcodeRows) {
      const barcode = row.barcode.trim();
      if (barcode === '') continue;
      if (barcode.length < BARCODE_MIN || barcode.length > BARCODE_MAX) {
        return t('items.errBarcodeLength');
      }
    }
    // IT-S1: the per-warehouse figures obey the same bound as the item's own.
    for (const row of warehouseLevelRows) {
      for (const value of [row.reorderPoint, row.reorderQty, row.overstockCeiling]) {
        if (value.trim() !== '' && !QTY0_RE.test(value.trim())) {
          return t('items.errNegativeReorder');
        }
      }
    }
    if (multiTier) {
      for (const row of tierRows) {
        if (row.price.trim() !== '' && !isValidTierPrice(row.price.trim())) {
          return t('items.errNegativePrice');
        }
      }
    }
    if (hasVariants) {
      for (const row of variantRows) {
        const touched = row.sku.trim() !== '' || row.attributes.trim() !== '';
        if (!touched) continue;
        if (row.sku.trim() === '') return t('items.errSku');
        if (row.attributes.trim() === '') return t('items.errAttributes');
        if (row.priceOverride.trim() !== '' && !MONEY_RE.test(row.priceOverride.trim())) {
          return t('items.errMoney');
        }
      }
    }
    if (isBundle) {
      const real = componentRows.filter((row) => row.componentItemId !== '');
      if (real.length === 0) return t('items.errKitNeedsComponents');
      for (const row of real) {
        if (!/^\d{1,12}(\.\d{1,3})?$/.test(row.qty.trim()) || /^0+(\.0+)?$/.test(row.qty.trim())) {
          return t('items.errQty');
        }
      }
    }
    return null;
  };

  const submit = async (values: ItemFormValues, confirmBelowCost: boolean) => {
    setServerError(null);
    setWarnings([]);
    const childProblem = validateChildren();
    setChildError(childProblem);
    if (childProblem) return;
    const request: CreateItemRequest = {
      ...toItemRequest(
        values,
        unitRows,
        values.multiTier ? tierRows : tierRows,
        hasVariants ? variantRows : [],
        componentRows,
        canSeeCost,
        barcodeRows,
        warehouseLevelRows,
      ),
      // BR-MD-05-08: sent only after the question has been answered. The
      // server reads the approval itself — this is the confirmation, not the
      // authority (ItemsService.assertBelowCostAllowed).
      ...(confirmBelowCost ? { confirmBelowCost: true } : {}),
    };
    try {
      if (isEdit) {
        const result = await updateItem.mutateAsync({
          ...request,
          // An emptied mapping box UN-MAPS the item (the update contract
          // takes null; create simply omits the field).
          ...(values.inventoryAccountCode.trim() === '' ? { inventoryAccountCode: null } : {}),
          // Same shape: "Not stated" on an edit TAKES THE MAKER OFF (IT-S6),
          // and an emptied twin / print name / specifications / price
          // currency box clears the stored value rather than keeping it.
          ...(values.manufacturerId === '' ? { manufacturerId: null } : {}),
          ...(values.nameAr.trim() === '' ? { nameAr: null } : {}),
          ...(values.printName.trim() === '' ? { printName: null } : {}),
          ...(values.specifications.trim() === '' ? { specifications: null } : {}),
          ...(values.priceCurrency.trim() === '' ? { priceCurrency: null } : {}),
        });
        setWarnings(result.warnings);
        if (result.warnings.length === 0) void navigate('/inventory');
      } else {
        const result = await createItem.mutateAsync(request);
        if (result.warnings.length > 0) {
          void navigate(`/inventory/items/${result.item.id}`, {
            state: { warnings: result.warnings },
          });
        } else {
          void navigate('/inventory');
        }
      }
      setBelowCostAsk(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // BR-MD-05-08 is a QUESTION, so it gets the amber confirm strip rather
      // than the red one. Everything else is an ordinary refusal. The
      // RECOGNITION reads the wire's English sentence (stable by D46's
      // compatibility promise); what the strip SHOWS is localized — the
      // cause carries key+params, so the question reaches an Arabic reader
      // in Arabic with the item's name embedded.
      if (isBelowCostQuestion(message)) {
        setBelowCostAsk(localizeServerMessage(t, message, error));
        return;
      }
      setBelowCostAsk(null);
      setServerError(localizeServerMessage(t, message, error));
    }
  };

  const onSubmit = form.handleSubmit((values) => submit(values, false));

  if (isEdit && loadingExisting) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  // §6: Save greyed until Name, Category and Stock unit — never the code. The
  // PERMISSION half moved out to <Gated>, which greys the same button but says
  // why (BR-PERM-17): disabled-and-silent was the one shape 16.7 rules out.
  const saveDisabled = !name.trim() || !categoryId || !stockUnitId || submitting;

  const unitChoices = (units ?? []).filter((unit) => unit.isActive);
  const listedUnitIds = [stockUnitId, ...unitRows.map((row) => row.unitId)].filter(Boolean);

  // BR-MD-03-04/-04b/-05 — once stock has moved, the shape of the movement is
  // history: the stock unit, the factors it was measured with, and the
  // tracking type are all frozen. A new item is the way to change them.
  const lockedByMovements = Boolean(existing?.hasMovements);
  const storedUnitIds = new Set((existing?.units ?? []).map((row) => row.unitId));

  return (
    <form noValidate onSubmit={onSubmit} className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold">{t(isEdit ? 'items.editTitle' : 'items.newTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {isEdit && existing ? <bdi dir="ltr">{existing.code}</bdi> : t('items.codeNew')}
        </span>
        {isEdit && existing ? (
          <span className="text-sm">
            {existing.deletedAt !== null ? (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                {t('items.deleted')}
              </span>
            ) : existing.isActive ? null : (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {t('common.inactive')}
              </span>
            )}
          </span>
        ) : null}
        <div className="ms-auto flex flex-wrap gap-2">
          {/* S4 (IT-N1/N2/N5/N7) — the shared header rail: ‹‹ ›› by code, Go
              to code, Find, New. The form's own dirty flag holds the arrows. */}
          <DocumentHeaderControls kind="item" id={itemId} dirty={form.formState.isDirty} />
          {/* IT-N4: a deleted item offers Restore and nothing else to change
              its state; an ordinary one offers Delete (asked first, through
              the product's one confirm box) beside the switch. */}
          {isEdit && existing && existing.deletedAt !== null ? (
            <Gated permission="inventory.edit" reasonKey="perm.cannot.editItems">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setServerError(null);
                  itemDelete
                    .mutateAsync('restore')
                    .catch((error: unknown) =>
                      setServerError(
                        localizeServerMessage(
                          t,
                          error instanceof Error ? error.message : String(error),
                          error,
                        ),
                      ),
                    );
                }}
              >
                {t('items.restore')}
              </Button>
            </Gated>
          ) : null}
          {isEdit && existing && existing.deletedAt === null ? (
            <Gated permission="inventory.delete" reasonKey="perm.cannot.deleteItems">
              <Button type="button" variant="outline" onClick={() => setConfirmingDelete(true)}>
                {t('common.delete')}
              </Button>
            </Gated>
          ) : null}
          {isEdit && existing && existing.deletedAt === null ? (
            <Gated permission="inventory.edit" reasonKey="perm.cannot.editItems">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setServerError(null);
                itemActive
                  .mutateAsync(existing.isActive ? 'deactivate' : 'reactivate')
                  .catch((error: unknown) =>
                    // The cause rides along: a structured refusal (D46 —
                    // stockAboveZero names the quantity) localizes by
                    // key+params rather than the exact-match map.
                    setServerError(
                      localizeServerMessage(
                        t,
                        error instanceof Error ? error.message : String(error),
                        error,
                      ),
                    ),
                  );
              }}
            >
              {t(existing.isActive ? 'items.switchOff' : 'items.switchOn')}
            </Button>
            </Gated>
          ) : null}
          {isEdit && existing ? (
            <Gated permission="inventory.create" reasonKey="perm.cannot.createItems">
              <Button asChild variant="outline">
                <Link to={`/inventory/items/new?copy=${existing.id}`}>
                  <Copy aria-hidden /> {t('items.newSimilar')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          <Button asChild variant="outline">
            <Link to="/inventory">{t('common.cancel')}</Link>
          </Button>
          <Gated
            permission={isEdit ? 'inventory.edit' : 'inventory.create'}
            reasonKey={isEdit ? 'perm.cannot.editItems' : 'perm.cannot.createItems'}
          >
            <Button type="submit" disabled={saveDisabled}>
              {submitting ? <Loader2 className="animate-spin" aria-hidden /> : null}
              {t('common.save')}
            </Button>
          </Gated>
        </div>
      </div>

      {/* IT-N4 — GEN-58's box: the verb, the what-will-happen half, no OK. */}
      {isEdit && existing ? (
        <DeleteConfirmBox
          open={confirmingDelete}
          detail={t('items.confirmDelete', { name: existing.name })}
          busy={itemDelete.isPending}
          onConfirm={() => {
            setServerError(null);
            itemDelete
              .mutateAsync('delete')
              .then(() => {
                setConfirmingDelete(false);
                void navigate('/inventory');
              })
              .catch((error: unknown) => {
                setConfirmingDelete(false);
                // D46: stockAboveZero arrives structured — the quantity and
                // unit reach an Arabic reader in Arabic.
                setServerError(
                  localizeServerMessage(
                    t,
                    error instanceof Error ? error.message : String(error),
                    error,
                  ),
                );
              });
          }}
          onCancel={() => setConfirmingDelete(false)}
        />
      ) : null}
      {serverError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {serverError}
        </p>
      ) : null}
      {childError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {childError}
        </p>
      ) : null}
      {warnings.length > 0 ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
          <p className="font-medium">{t('items.savedWarnings')}</p>
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      ) : null}
      {/* BR-MD-05-08 / ERR-MD-05-03 — a question, not a verdict, so GEN-155's
          amber strip rather than GEN-153's red one. The Continue button
          exists only for MD-05 §2's Manager; everybody else gets §14's own
          "What the user should do", which is to ask one. The server refuses
          either way — this decides what the screen offers, never what it
          allows. */}
      {belowCostAsk !== null ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm"
        >
          <p className="font-medium">{belowCostAsk}</p>
          {canApproveBelowCost ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void submit(form.getValues(), true)}
            >
              {t('items.belowCostContinue')}
            </Button>
          ) : (
            <p>{localizeServerMessage(t, ITEM_MESSAGES.belowCostNeedsManager)}</p>
          )}
        </div>
      ) : null}

      {/* Panel 1 — Core identification */}
      <Card>
        <CardHeader>
          <CardTitle>{t('items.secCore')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="i-code">{t('items.fCode')}</Label>
            <div className="flex items-center gap-3">
              {/* Field 1: Auto on = greyed, allocated on save; off = typed. */}
              <Input
                id="i-code"
                dir="ltr"
                maxLength={40}
                className="font-mono"
                disabled={codeAuto}
                placeholder={codeAuto ? t('items.codeNew') : ''}
                {...form.register('code')}
              />
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={codeAuto}
                  onCheckedChange={(checked) => form.setValue('codeAuto', checked === true)}
                />
                {t('items.codeAuto')}
              </Label>
            </div>
          </div>
          <Field id="i-name" label={t('items.fName')} error={errors.name?.message}>
            <Input
              id="i-name"
              maxLength={200}
              aria-invalid={Boolean(errors.name)}
              {...form.register('name')}
            />
          </Field>
          {/* IT-C1 — the Arabic twin and the printed name, beside Name. `name`
              stays the display fallback and what every rule reads (S5). */}
          <Field id="i-name-ar" label={t('items.fNameAr')}>
            <Input id="i-name-ar" dir="rtl" lang="ar" maxLength={200} {...form.register('nameAr')} />
          </Field>
          <Field id="i-print-name" label={t('items.fPrintName')} hint={t('items.fPrintNameHint')}>
            <Input id="i-print-name" maxLength={200} {...form.register('printName')} />
          </Field>
          <div className="grid gap-1.5">
            <span className="text-sm font-medium leading-none">{t('items.fType')}</span>
            {/* Field 3: three buttons, not a dropdown. Bundle reveals the kit
                panel; Service hides stock and tracking. */}
            <div className="flex gap-2" role="group" aria-label={t('items.fType')}>
              {(['product', 'service', 'bundle'] as const).map((choice) => (
                <Button
                  key={choice}
                  type="button"
                  variant={type === choice ? 'default' : 'outline'}
                  onClick={() => form.setValue('type', choice)}
                >
                  {t(`items.type.${choice}`)}
                </Button>
              ))}
            </div>
          </div>
          <Field id="i-category" label={t('items.fCategory')} error={errors.categoryId?.message}>
            {/* GEN-51 (D26): a category is one of the four simple records, so
                the create row makes one here rather than sending the person
                away from a half-typed item.

                The new category takes the same path a picked one does —
                `onCategoryChange` — which also carries MD-04 §5's default-unit
                and default-tax fill. Those two do NOTHING for a record made in
                this window, and that is correct rather than a gap: the window
                creates a category with a code and a name only, so it HAS no
                default unit to bring across. The fill is not skipped for it;
                there is simply nothing to fill. A category that needs those
                defaults is edited on its own screen, where they exist. */}
            <QuickCreateSelect
              entity="itemCategory"
              id="i-category"
              value={categoryId}
              onChange={onCategoryChange}
            >
              <option value="" />
              {(categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </QuickCreateSelect>
          </Field>
          {/* IT-S6: a plain select over the makers master plus a way to the
              master's own screen — the roster's quick-create window is for the
              four D26 records, and this list is maintained on its own tab. */}
          <Field id="i-manufacturer" label={t('items.fManufacturer')}>
            <div className="flex items-center gap-3">
              <NativeSelect id="i-manufacturer" {...form.register('manufacturerId')}>
                <option value="">{t('items.manufacturerNone')}</option>
                {(manufacturers ?? [])
                  .filter((maker) => maker.isActive || maker.id === existing?.manufacturerId)
                  .map((maker) => (
                    <option key={maker.id} value={maker.id}>
                      {localizedName(maker, i18n.language)}
                    </option>
                  ))}
              </NativeSelect>
              <Link to="/inventory/manufacturers" className="shrink-0 text-sm hover:underline">
                {t('items.manageManufacturers')}
              </Link>
            </div>
          </Field>
          {/* IT-S5 — a statement about the item's life, separate from the
              switch (a discontinued item stays pickable while the shelf has some). */}
          <Field id="i-availability" label={t('items.fAvailability')}>
            <NativeSelect id="i-availability" {...form.register('availability')}>
              {ITEM_AVAILABILITY.map((choice) => (
                <option key={choice} value={choice}>
                  {t(`items.availability.${choice}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {/* IT-S7 — OFF takes the item off the till's grid only. */}
          <Label className="flex items-center gap-2 self-end pb-2 font-normal">
            <Checkbox
              checked={form.watch('posVisible')}
              onCheckedChange={(checked) => form.setValue('posVisible', checked === true)}
            />
            {t('items.fPosVisible')}
          </Label>
          <div className="sm:col-span-2">
            <Field id="i-description" label={t('items.fDescription')}>
              <Textarea id="i-description" maxLength={1000} {...form.register('description')} />
            </Field>
          </div>
          {/* IT-S9 — a datasheet paragraph; free text on purpose. */}
          <div className="sm:col-span-2">
            <Field id="i-specifications" label={t('items.fSpecifications')}>
              <Textarea
                id="i-specifications"
                maxLength={4000}
                rows={4}
                {...form.register('specifications')}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/*
        Barcodes (bugs: New Item — IT-C2 "move Barcode out of Core into its
        own section", IT-N9 "multiple barcodes per unit: Unit / Barcode /
        Size / Notes"). The first row is the primary the till matches
        (SAL-06 §5c); the rest are the pack and case codes printed for the
        item's other units. `dir="ltr"` and `font-mono` on the code box for the
        same reason the item code has them: digits read off a label must not
        be re-ordered by an Arabic page's direction. Nothing here is required
        and nothing gates Save — most of a catalogue is never scanned.
      */}
      <Card>
        <CardHeader>
          <CardTitle>{t('items.secBarcodes')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2">
          {barcodeRows.length > 0 ? (
            <div className="hidden gap-2 text-xs text-muted-foreground sm:grid sm:grid-cols-[10rem_14rem_8rem_1fr_2.5rem]">
              <span>{t('items.colBarcodeUnit')}</span>
              <span>{t('items.fBarcode')}</span>
              <span>{t('items.colBarcodeSize')}</span>
              <span>{t('items.colBarcodeNotes')}</span>
              <span />
            </div>
          ) : null}
          {barcodeRows.map((row, index) => {
            const update = (patch: Partial<BarcodeRowState>) =>
              setBarcodeRows((current) =>
                current.map((each, at) => (at === index ? { ...each, ...patch } : each)),
              );
            return (
              <div
                key={index}
                className="grid items-center gap-2 sm:grid-cols-[10rem_14rem_8rem_1fr_2.5rem]"
              >
                {/* The item's OWN units: the stock unit and the conversion rows. */}
                <NativeSelect
                  aria-label={t('items.cellBarcodeUnit', { line: index + 1 })}
                  value={row.unitId}
                  onChange={(event) => update({ unitId: event.target.value })}
                  className="h-10 text-sm"
                >
                  <option value="">{t('items.barcodeUnitAny')}</option>
                  {(units ?? [])
                    .filter((unit) => listedUnitIds.includes(unit.id))
                    .map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                </NativeSelect>
                <Input
                  aria-label={t('items.cellBarcode', { line: index + 1 })}
                  dir="ltr"
                  inputMode="numeric"
                  maxLength={BARCODE_MAX}
                  className="h-10 font-mono"
                  placeholder={t('items.fBarcode')}
                  value={row.barcode}
                  onChange={(event) => update({ barcode: event.target.value })}
                />
                <Input
                  aria-label={t('items.cellBarcodeSize', { line: index + 1 })}
                  maxLength={40}
                  className="h-10"
                  placeholder={t('items.colBarcodeSize')}
                  value={row.size}
                  onChange={(event) => update({ size: event.target.value })}
                />
                <Input
                  aria-label={t('items.cellBarcodeNotes', { line: index + 1 })}
                  maxLength={200}
                  className="h-10"
                  placeholder={t('items.colBarcodeNotes')}
                  value={row.notes}
                  onChange={(event) => update({ notes: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.remove')}
                  title={t('common.remove')}
                  onClick={() => setBarcodeRows(barcodeRows.filter((_, at) => at !== index))}
                >
                  <X aria-hidden />
                </Button>
              </div>
            );
          })}
          <Button
            type="button"
            variant="outline"
            className="justify-self-start"
            onClick={() =>
              setBarcodeRows([...barcodeRows, { unitId: '', barcode: '', size: '', notes: '' }])
            }
          >
            <Plus aria-hidden /> {t('items.addBarcode')}
          </Button>
        </CardContent>
      </Card>

      {/* Panel 2 — Kit components (Bundle only, owner ruling R7) */}
      {isBundle ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('items.secKit')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={form.watch('autoDeduct')}
                onCheckedChange={(checked) => form.setValue('autoDeduct', checked === true)}
              />
              {t('items.autoDeduct')}
            </Label>
            <div className="grid gap-2">
              {componentRows.map((row, index) => {
                const component = componentById.get(row.componentItemId);
                const componentUnits = component
                  ? [
                      { id: component.stockUnitId, label: component.stockUnitName },
                      ...component.units.map((unit) => ({ id: unit.unitId, label: unit.unitName })),
                    ]
                  : [];
                return (
                  <div key={index} className="flex flex-wrap items-center gap-2">
                    {/* Field 8a: a REAL item reference — never typed text. */}
                    <div className="w-64">
                      <ItemCell
                        label={t('items.cellComponent', { line: index + 1 })}
                        clearLabel={t('items.pickItem')}
                        picker={componentPicker}
                        value={row.componentItemId}
                        onPick={(componentItemId) => {
                          const next = [...componentRows];
                          const picked = componentById.get(componentItemId);
                          next[index] = {
                            componentItemId,
                            qty: row.qty,
                            unitId: picked?.stockUnitId ?? '',
                          };
                          setComponentRows(next);
                        }}
                      />
                    </div>
                    <Input
                      aria-label={t('items.cellQtyPerKit', { line: index + 1 })}
                      dir="ltr"
                      inputMode="decimal"
                      className="h-10 w-24 font-mono"
                      value={row.qty}
                      onChange={(event) => {
                        const next = [...componentRows];
                        next[index] = { ...row, qty: event.target.value };
                        setComponentRows(next);
                      }}
                    />
                    {/*
                      THE NAME A SCREEN READER HEARS IS NOT THE COLUMN HEADING.
                      On a Bundle, this control and the conversion table's unit
                      picker in panel 5 are both on screen, and MD-03 names both
                      columns "Unit" — so a screen-reader user heard "Unit"
                      twice, meaning two different things, with nothing to tell
                      them apart. The visible heading stays as the spec writes
                      it; the accessible name says WHICH unit, the same way
                      every line-grid cell in the product already does
                      ("Quantity, line 3"). Panel 5's is "Conversion unit".
                    */}
                    <NativeSelect
                      aria-label={t('items.cellComponentUnit', { line: index + 1 })}
                      value={row.unitId}
                      onChange={(event) => {
                        const next = [...componentRows];
                        next[index] = { ...row, unitId: event.target.value };
                        setComponentRows(next);
                      }}
                      className="h-10 w-32 text-sm"
                    >
                      {componentUnits.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.label}
                        </option>
                      ))}
                    </NativeSelect>
                    {/* Field 8d: a statement read from the component, not a control. */}
                    <span className="w-16 text-xs text-muted-foreground">
                      {component
                        ? component.trackingType === 'none'
                          ? '—'
                          : t(`items.tracking.${component.trackingType}`)
                        : ''}
                    </span>
                    <span className="w-24 text-end font-mono text-xs text-muted-foreground">
                      {component?.costPrice ? (
                        <Amount
                          value={multiplyQtyByFactor(row.qty || '0', component.costPrice, 2)}
                          currency={baseCurrency}
                        />
                      ) : null}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('common.remove')}
                      onClick={() =>
                        setComponentRows(componentRows.filter((_, at) => at !== index))
                      }
                    >
                      <X aria-hidden />
                    </Button>
                  </div>
                );
              })}
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setComponentRows([
                      ...componentRows,
                      { componentItemId: '', qty: '1', unitId: '' },
                    ])
                  }
                >
                  <Plus aria-hidden /> {t('items.addComponent')}
                </Button>
                {/* Field 8e: the total beside the kit's own price. */}
                <span className="text-sm text-muted-foreground">
                  {t('items.componentsTotal')}:{' '}
                  <Amount value={componentsTotal} currency={baseCurrency} className="font-medium" />
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Panel 3 — Variants */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('items.secVariants')}</CardTitle>
          <Label className="flex items-center gap-2 font-normal">
            <Checkbox
              checked={hasVariants}
              onCheckedChange={(checked) => setHasVariants(checked === true)}
            />
            {t('items.hasVariants')}
          </Label>
        </CardHeader>
        {hasVariants ? (
          <CardContent className="grid gap-2">
            {/* bugs: New Item — "explain in detail how this works". */}
            <p className="text-sm text-muted-foreground">{t('items.variantsHelp')}</p>
            {variantRows.map((row, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label={t('items.colSku')}
                  dir="ltr"
                  maxLength={60}
                  className="h-10 w-44 font-mono"
                  placeholder={t('items.colSku')}
                  value={row.sku}
                  onChange={(event) => {
                    const next = [...variantRows];
                    next[index] = { ...row, sku: event.target.value };
                    setVariantRows(next);
                  }}
                />
                <Input
                  aria-label={t('items.colAttributes')}
                  maxLength={120}
                  className="h-10 w-52"
                  placeholder={t('items.colAttributes')}
                  value={row.attributes}
                  onChange={(event) => {
                    const next = [...variantRows];
                    next[index] = { ...row, attributes: event.target.value };
                    setVariantRows(next);
                  }}
                />
                {/* Rule 3: an empty override INHERITS, and the box says so. */}
                <Input
                  aria-label={t('items.colPriceOverride')}
                  dir="ltr"
                  inputMode="decimal"
                  className="h-10 w-28 font-mono"
                  placeholder={t('items.inheritsPh')}
                  value={row.priceOverride}
                  onChange={(event) => {
                    const next = [...variantRows];
                    next[index] = { ...row, priceOverride: event.target.value };
                    setVariantRows(next);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.remove')}
                  onClick={() => setVariantRows(variantRows.filter((_, at) => at !== index))}
                >
                  <X aria-hidden />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className="justify-self-start"
              onClick={() =>
                setVariantRows([...variantRows, { sku: '', attributes: '', priceOverride: '' }])
              }
            >
              <Plus aria-hidden /> {t('items.addVariant')}
            </Button>
          </CardContent>
        ) : null}
      </Card>

      {/* Panel 4 — Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>{t('items.secPricing')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          {/* bugs: New Item › Pricing — "align the boxes". Two rows of two:
              Unit price | Cost price, then Tax | Inventory account. Each row
              is its own grid, so a hidden Cost (Service, or no See-item-cost)
              never pulls Tax up beside the price; `items-start` keeps a
              validation message under one box from shifting its neighbour. */}
          <div className="grid items-start gap-4 sm:grid-cols-3">
            <Field id="i-price" label={t('items.fUnitPrice')} error={errors.unitPrice?.message}>
              <Input
                id="i-price"
                dir="ltr"
                inputMode="decimal"
                placeholder="0.00"
                className="font-mono"
                aria-invalid={Boolean(errors.unitPrice)}
                {...form.register('unitPrice')}
              />
            </Field>
            {/* IT-S3 — what the price (and the tier prices) are IN. Blank =
                the company's base currency (plan §12). The warehouse's own
                free-text-plus-suggestions box, so a code the tenant has not
                added is warned about here and refused by the server. */}
            <div className="grid gap-1.5">
              <CurrencyField
                id="i-price-currency"
                label={t('items.fPriceCurrency')}
                value={priceCurrency}
                onChange={(next) => form.setValue('priceCurrency', next, { shouldDirty: true })}
                options={currencyCodes}
              />
              <p className="text-xs text-muted-foreground">
                {priceCurrency.trim() === ''
                  ? t('items.priceCurrencyBase', { code: baseCurrency })
                  : currencyNotOffered(priceCurrency, currencyCodes)
                    ? t('items.priceCurrencyNotOffered', { code: priceCurrency.trim().toUpperCase() })
                    : null}
              </p>
            </div>
            {!isService && canSeeCost ? (
              <div className="grid gap-1.5">
                {/* "Hide cost" sits ON the field's label row and the mask IS the
                    reveal (bugs: New Item › Pricing — "move Hide cost next to
                    the name, remove Show the cost price"). Revealing is still a
                    view-only act: it never touches the stored hideCost. */}
                <div className="flex min-h-[1lh] flex-wrap items-center gap-x-4 gap-y-1">
                  <Label htmlFor="i-cost">
                    {t('items.fCostPrice')}{' '}
                    <span className="text-xs text-muted-foreground">({t('items.costInternal')})</span>
                  </Label>
                  <Label className="ms-auto flex items-center gap-2 text-sm font-normal">
                    <Checkbox
                      checked={hideCost}
                      onCheckedChange={(checked) => {
                        form.setValue('hideCost', checked === true);
                        if (checked !== true) setCostRevealed(false);
                      }}
                    />
                    {t('items.fHideCost')}
                  </Label>
                </div>
                {/* Field 13: Hide cost is ON by default — masked until revealed. */}
                {hideCost && !costRevealed ? (
                  <button
                    type="button"
                    aria-label={t('items.revealCost')}
                    title={t('items.revealCost')}
                    onClick={() => setCostRevealed(true)}
                    className="flex h-12 items-center rounded-md border bg-muted px-3 text-start font-mono text-sm transition-colors hover:bg-muted/70"
                  >
                    ••••••
                  </button>
                ) : (
                  <Input
                    id="i-cost"
                    dir="ltr"
                    inputMode="decimal"
                    placeholder="0.0000"
                    className="font-mono"
                    aria-invalid={Boolean(errors.costPrice)}
                    {...form.register('costPrice')}
                  />
                )}
                {errors.costPrice ? (
                  <p className="text-sm text-destructive">{t(errors.costPrice.message ?? '')}</p>
                ) : null}
                {/* IT-S2 — what the shelves actually cost, read-only: the
                    movement writer's weighted average across every warehouse.
                    Beside the typed cost so the two can be compared; absent
                    on a new item (nothing is held yet). */}
                {isEdit && existing?.currentCost !== undefined ? (
                  <p className="text-xs text-muted-foreground">
                    {t('items.currentCost')}:{' '}
                    {/* 4dp, like the typed cost (GEN-102) — Amount would round it to money. */}
                    <Ltr value={existing.currentCost} className="font-mono font-medium text-foreground" />{' '}
                    {baseCurrency}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <Field id="i-tax" label={t('items.fTaxCategory')}>
              {/* TODO(md-07): becomes a picker over the taxes master. */}
              <Input id="i-tax" maxLength={60} {...form.register('taxCategory')} />
            </Field>
            {/* INV-08 §12's per-item inventory mapping: the chart account
                CODE holding this item's stock value. Empty = the category's
                mapping, else 1131 Raw Materials. */}
            <Field id="i-inventory-account" label={t('items.fInventoryAccount')}>
              <Input
                id="i-inventory-account"
                dir="ltr"
                maxLength={20}
                placeholder={t('items.fInventoryAccountPh')}
                {...form.register('inventoryAccountCode')}
              />
            </Field>
          </div>

          <Label className="flex items-center gap-2 font-normal">
            <Checkbox
              checked={multiTier}
              onCheckedChange={(checked) => form.setValue('multiTier', checked === true)}
            />
            {t('items.fMultiTier')}
          </Label>
          {/* Field 16 (R5): one row per tier the tenant has DEFINED — one,
              three or eleven; an empty box means no special price. */}
          {multiTier ? (
            <div className="grid max-w-md gap-2">
              {tierRows.map((row) => (
                <div key={row.tierId} className="flex items-center gap-3">
                  <span className="w-40 text-sm">{row.tierName}</span>
                  <Input
                    aria-label={row.tierName}
                    dir="ltr"
                    inputMode="decimal"
                    placeholder={t('items.tierEmptyPh')}
                    className="h-10 font-mono"
                    value={row.price}
                    onChange={(event) =>
                      setTierRows(setTierPrice(tierRows, row.tierId, event.target.value))
                    }
                  />
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Panel 5 — Units of measure (owner ruling R6) */}
      <Card>
        <CardHeader>
          <CardTitle>{t('items.secUnits')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            {/* BR-MD-03-04: once a movement exists the stock unit is history. */}
            <Field
              id="i-stock-unit"
              label={t('items.fStockUnit')}
              {...(lockedByMovements ? { hint: t('items.lockedByMovements') } : {})}
            >
              {/* `form.register` traded for watch+setValue so the create row
                  can be offered here: `register` owns the change handler, and
                  the sentinel has to be intercepted before the form store
                  ever sees it. `stockUnitId` was already being watched (the
                  category's default-unit effect reads it), so this is the
                  same value from the same source. */}
              <QuickCreateSelect
                entity="unit"
                id="i-stock-unit"
                disabled={lockedByMovements}
                value={stockUnitId}
                onChange={(next) =>
                  form.setValue('stockUnitId', next, { shouldDirty: true, shouldValidate: true })
                }
              >
                <option value="" />
                {unitChoices.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </QuickCreateSelect>
            </Field>
            <Field id="i-buy-default" label={t('items.fDefaultBuyUnit')}>
              <NativeSelect id="i-buy-default" {...form.register('defaultBuyUnitId')}>
                <option value="">{t('items.stockUnitOption')}</option>
                {(units ?? [])
                  .filter((unit) => listedUnitIds.includes(unit.id) && unit.id !== stockUnitId)
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
              </NativeSelect>
            </Field>
            <Field id="i-sell-default" label={t('items.fDefaultSellUnit')}>
              <NativeSelect id="i-sell-default" {...form.register('defaultSellUnitId')}>
                <option value="">{t('items.stockUnitOption')}</option>
                {(units ?? [])
                  .filter((unit) => listedUnitIds.includes(unit.id) && unit.id !== stockUnitId)
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
              </NativeSelect>
            </Field>
          </div>

          <div className="grid gap-2">
            {/* Field 18: the stock unit as a fixed first row with a Base pill. */}
            {stockUnit ? (
              <div className="flex items-center gap-3 text-sm">
                <span className="w-40">{stockUnit.name}</span>
                <Ltr value="1" className="w-24 font-mono" />
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground">
                  {t('items.unitsBase')}
                </span>
              </div>
            ) : null}
            {unitRows.map((row, index) => {
              const unit = (units ?? []).find((choice) => choice.id === row.unitId);
              return (
                <div key={index} className="flex flex-wrap items-center gap-3">
                  {/* The other half of the pair above: "Conversion unit, row 2". */}
                  {/* GEN-51 here too (bugs: "add a new unit here"): a missing
                      conversion unit is created in place, not by leaving. */}
                  <QuickCreateSelect
                    entity="unit"
                    aria-label={t('items.cellConversionUnit', { line: index + 1 })}
                    value={row.unitId}
                    onChange={(nextUnitId) => {
                      setUnitRows((current) =>
                        current.map((each, at) =>
                          at === index ? { ...each, unitId: nextUnitId } : each,
                        ),
                      );
                    }}
                    className="h-10 w-40 text-sm"
                  >
                    <option value="" />
                    {unitChoices
                      .filter((choice) => choice.id !== stockUnitId)
                      .map((choice) => (
                        <option key={choice.id} value={choice.id}>
                          {choice.name}
                        </option>
                      ))}
                  </QuickCreateSelect>
                  <Input
                    aria-label={t('items.cellFactor', { line: index + 1 })}
                    dir="ltr"
                    inputMode="decimal"
                    className="h-10 w-24 font-mono"
                    value={row.factor}
                    // BR-MD-03-04b: a factor posted movements already used.
                    disabled={lockedByMovements && storedUnitIds.has(row.unitId)}
                    onChange={(event) => {
                      const next = [...unitRows];
                      next[index] = { ...row, factor: event.target.value };
                      setUnitRows(next);
                    }}
                  />
                  {lockedByMovements && storedUnitIds.has(row.unitId) ? (
                    <span className="text-xs text-muted-foreground">
                      {t('items.lockedByMovements')}
                    </span>
                  ) : null}
                  <Label className="flex items-center gap-1.5 text-sm font-normal">
                    <Checkbox
                      checked={row.allowBuy}
                      onCheckedChange={(checked) => {
                        const next = [...unitRows];
                        next[index] = { ...row, allowBuy: checked === true };
                        setUnitRows(next);
                      }}
                    />
                    {t('items.colBuy')}
                  </Label>
                  <Label className="flex items-center gap-1.5 text-sm font-normal">
                    <Checkbox
                      checked={row.allowSell}
                      onCheckedChange={(checked) => {
                        const next = [...unitRows];
                        next[index] = { ...row, allowSell: checked === true };
                        setUnitRows(next);
                      }}
                    />
                    {t('items.colSell')}
                  </Label>
                  {/* Field 19: the row reads back as a sentence. */}
                  {unit && stockUnit && isValidFactor(row.factor) ? (
                    <span className="text-xs text-muted-foreground">
                      {t('items.factorSentence', {
                        unit: unit.name,
                        factor: trimZeros(row.factor),
                        stock: stockUnit.name,
                      })}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('common.remove')}
                    onClick={() => setUnitRows(unitRows.filter((_, at) => at !== index))}
                  >
                    <X aria-hidden />
                  </Button>
                </div>
              );
            })}
            <Button
              type="button"
              variant="outline"
              className="justify-self-start"
              onClick={() =>
                setUnitRows([...unitRows, { unitId: '', factor: '', allowBuy: true, allowSell: true }])
              }
            >
              <Plus aria-hidden /> {t('items.addUnit')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Panel 6 — Inventory & stock (not for Service) */}
      {!isService ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('items.secStock')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                id="i-reorder-point"
                label={t('items.fReorderPoint')}
                error={errors.reorderPoint?.message}
              >
                <Input
                  id="i-reorder-point"
                  dir="ltr"
                  inputMode="decimal"
                  placeholder="0"
                  className="font-mono"
                  aria-invalid={Boolean(errors.reorderPoint)}
                  {...form.register('reorderPoint')}
                />
              </Field>
              <Field
                id="i-reorder-qty"
                label={t('items.fReorderQty')}
                error={errors.reorderQty?.message}
              >
                <Input
                  id="i-reorder-qty"
                  dir="ltr"
                  inputMode="decimal"
                  placeholder="0"
                  className="font-mono"
                  aria-invalid={Boolean(errors.reorderQty)}
                  {...form.register('reorderQty')}
                />
              </Field>
              {/* PART 8 §8.1 — the Overstock line, measured on ON-HAND. */}
              <Field
                id="i-overstock"
                label={t('items.fOverstockCeiling')}
                error={errors.overstockCeiling?.message}
              >
                <Input
                  id="i-overstock"
                  dir="ltr"
                  inputMode="decimal"
                  className="font-mono"
                  aria-invalid={Boolean(errors.overstockCeiling)}
                  {...form.register('overstockCeiling')}
                />
              </Field>
            </div>
            {/* bugs: Items — "some items' stock doesn't matter". OFF leaves the
                figures alone and takes the item out of the Low/Out KPIs and
                the status badge. */}
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={form.watch('trackStockAlerts')}
                onCheckedChange={(checked) => form.setValue('trackStockAlerts', checked === true)}
              />
              {t('items.fTrackStockAlerts')}
            </Label>
            {/* Fields 22–25: the LIVE per-location figures, read from
                stock_levels on every read — never typed, never stored here. */}
            {isEdit && existing ? (
              existing.stock.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('items.stockNone')}</p>
              ) : (
                <div className="grid gap-2">
                  <h4 className="text-sm font-semibold">{t('items.stockByWarehouse')}</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="py-2 text-start font-medium">{t('items.colWarehouse')}</th>
                        <th className="py-2 text-end font-medium">{t('items.colStockOnHand')}</th>
                        <th className="py-2 text-end font-medium">{t('items.colStockReserved')}</th>
                        <th className="py-2 text-end font-medium">{t('items.colStockAvailable')}</th>
                        {/* IT-S1: the three editable overrides, per location. */}
                        <th className="py-2 ps-3 text-start font-medium">{t('items.fReorderPoint')}</th>
                        <th className="py-2 text-start font-medium">{t('items.fReorderQty')}</th>
                        <th className="py-2 text-start font-medium">{t('items.fOverstockCeiling')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existing.stock.map((row) => {
                        const level = warehouseLevelRows.find(
                          (wl) => wl.warehouseId === row.warehouseId,
                        ) ?? {
                          warehouseId: row.warehouseId,
                          reorderPoint: '',
                          reorderQty: '',
                          overstockCeiling: '',
                        };
                        const setLevel = (patch: Partial<WarehouseLevelRowState>) =>
                          setWarehouseLevelRows((current) =>
                            current.some((wl) => wl.warehouseId === row.warehouseId)
                              ? current.map((wl) =>
                                  wl.warehouseId === row.warehouseId ? { ...wl, ...patch } : wl,
                                )
                              : [...current, { ...level, ...patch }],
                          );
                        // The reorder point is the line the AVAILABLE figure
                        // crosses (BR-INV-05-01); 0 switches the check off. The
                        // warehouse's own point when it has one (IT-S1).
                        const watched = isBelowReorder(
                          row.available,
                          level.reorderPoint.trim() === ''
                            ? existing.reorderPoint
                            : level.reorderPoint.trim(),
                        );
                        return (
                          <tr
                            key={row.warehouseId}
                            className={cn(
                              'border-b last:border-0',
                              watched && 'bg-amber-50 dark:bg-amber-950/30',
                            )}
                          >
                            <TableCell className="px-0">{row.warehouseName}</TableCell>
                            {/* N-4: the decimals the item's STOCK unit allows,
                                and the unit beside every figure. This panel has
                                no Unit column of its own, so each number carries
                                it — one item, one unit, and N-14 wants it read
                                off the row rather than inferred. */}
                            <TableCell className="px-0 text-end">
                              <Qty
                                value={row.onHand}
                                precision={existing.stockUnitPrecision}
                                unit={existing.stockUnitSymbol}
                              />
                            </TableCell>
                            <TableCell className="px-0 text-end text-muted-foreground">
                              <Qty
                                value={row.reserved}
                                precision={existing.stockUnitPrecision}
                                unit={existing.stockUnitSymbol}
                              />
                            </TableCell>
                            <TableCell className="px-0 text-end">
                              <Qty
                                value={row.available}
                                precision={existing.stockUnitPrecision}
                                unit={existing.stockUnitSymbol}
                                className="font-semibold"
                              />
                            </TableCell>
                            {/* Blank = the item's figure (the placeholder says which). */}
                            <TableCell className="ps-3 pe-1">
                              <Input
                                aria-label={t('items.cellWarehouseReorderPoint', {
                                  warehouse: row.warehouseName,
                                })}
                                dir="ltr"
                                inputMode="decimal"
                                className="h-9 w-24 font-mono"
                                placeholder={trimZeros(existing.reorderPoint)}
                                value={level.reorderPoint}
                                onChange={(event) => setLevel({ reorderPoint: event.target.value })}
                              />
                            </TableCell>
                            <TableCell className="px-1">
                              <Input
                                aria-label={t('items.cellWarehouseReorderQty', {
                                  warehouse: row.warehouseName,
                                })}
                                dir="ltr"
                                inputMode="decimal"
                                className="h-9 w-24 font-mono"
                                placeholder={trimZeros(existing.reorderQty)}
                                value={level.reorderQty}
                                onChange={(event) => setLevel({ reorderQty: event.target.value })}
                              />
                            </TableCell>
                            <TableCell className="px-1">
                              <Input
                                aria-label={t('items.cellWarehouseOverstock', {
                                  warehouse: row.warehouseName,
                                })}
                                dir="ltr"
                                inputMode="decimal"
                                className="h-9 w-24 font-mono"
                                placeholder={
                                  existing.overstockCeiling === null
                                    ? ''
                                    : trimZeros(existing.overstockCeiling)
                                }
                                value={level.overstockCeiling}
                                onChange={(event) =>
                                  setLevel({ overstockCeiling: event.target.value })
                                }
                              />
                            </TableCell>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {/* No reserved total by design — reservations belong to
                          documents, not to a company-wide number. */}
                      <tr className="font-semibold">
                        <TableCell className="px-0">{t('items.stockTotals')}</TableCell>
                        <TableCell className="px-0 text-end">
                          {/* N-14 permits this total: it adds one item's rows,
                              which are all in that item's stock unit. */}
                          <Qty
                            value={existing.totalOnHand}
                            precision={existing.stockUnitPrecision}
                            unit={existing.stockUnitSymbol}
                          />
                        </TableCell>
                        <TableCell />
                        <TableCell className="px-0 text-end">
                          <Qty
                            value={existing.totalAvailable}
                            precision={existing.stockUnitPrecision}
                            unit={existing.stockUnitSymbol}
                          />
                        </TableCell>
                        <TableCell colSpan={3} className="ps-3 text-xs font-normal text-muted-foreground">
                          {t('items.warehouseLevelsHint')}
                        </TableCell>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Panel 7 — Serial / batch tracking (not for Service) */}
      {!isService ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('items.secTracking')}</CardTitle>
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={trackingEnabled}
                onCheckedChange={(checked) => form.setValue('trackingEnabled', checked === true)}
              />
              {t('items.enableTracking')}
            </Label>
          </CardHeader>
          {trackingEnabled ? (
            <CardContent className="grid gap-4">
              <div className="grid gap-1.5">
                <span className="text-sm font-medium leading-none">{t('items.fTrackingType')}</span>
                <div className="flex gap-2" role="group" aria-label={t('items.fTrackingType')}>
                  {/* BR-MD-03-05 — frozen once the item has movements. */}
                  <Button
                    type="button"
                    variant={trackingType === 'serial' ? 'default' : 'outline'}
                    disabled={lockedByMovements}
                    onClick={() => form.setValue('trackingType', 'serial')}
                  >
                    {t('items.trackingSerialFull')}
                  </Button>
                  <Button
                    type="button"
                    variant={trackingType === 'batch' ? 'default' : 'outline'}
                    disabled={lockedByMovements}
                    onClick={() => form.setValue('trackingType', 'batch')}
                  >
                    {t('items.trackingBatchFull')}
                  </Button>
                </div>
                {lockedByMovements ? (
                  <p className="text-xs text-muted-foreground">{t('items.lockedByMovements')}</p>
                ) : null}
              </div>
              {/* §5's resolved inconsistency: batch fields hide for Serial —
                  serials are captured at receipt, not on the item. */}
              {trackingType === 'batch' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field id="i-batch" label={t('items.fBatchNo')}>
                    <Input id="i-batch" dir="ltr" maxLength={40} {...form.register('batchNo')} />
                  </Field>
                  <Field id="i-expiry" label={t('items.fExpiry')}>
                    <Input id="i-expiry" dir="ltr" type="date" {...form.register('expiryDate')} />
                  </Field>
                </div>
              ) : null}
            </CardContent>
          ) : null}
        </Card>
      ) : null}

      {/* Panel 8 — Supplier & purchasing */}
      <Card>
        <CardHeader>
          <CardTitle>{t('items.secSupplier')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <Field id="i-supplier" label={t('items.fPreferredSupplier')}>
            {/* Field 30, GEN-50: partners with Supplier ticked only, searched. */}
            <PartnerTypeahead
              entity="supplier"
              id="i-supplier"
              label={t('items.fPreferredSupplier')}
              value={form.watch('preferredSupplierId') || null}
              onChange={(id) =>
                form.setValue('preferredSupplierId', id ?? '', { shouldDirty: true })
              }
              options={supplierPicker.options}
              selected={supplierPicker.optionFor(form.watch('preferredSupplierId'))}
              query={supplierPicker.query}
              onQueryChange={supplierPicker.setQuery}
              clearLabel={t('items.supplierNone')}
              emptyLabel={t('picker.noMatchingSupplier')}
              loading={supplierPicker.loading}
            />
          </Field>
          <Field id="i-supplier-code" label={t('items.fSupplierItemCode')}>
            <Input id="i-supplier-code" dir="ltr" maxLength={40} {...form.register('supplierItemCode')} />
          </Field>
          <Field id="i-lead" label={t('items.fLeadTime')} error={errors.leadTimeDays?.message}>
            <Input
              id="i-lead"
              dir="ltr"
              inputMode="numeric"
              aria-invalid={Boolean(errors.leadTimeDays)}
              {...form.register('leadTimeDays')}
            />
          </Field>
        </CardContent>
      </Card>

      {/*
        MD-RULE-5's showing half — the record's own screen shows all four
        facts, the partner-detail placement. Until 20260902100000 the items
        wire carried `createdAt` alone; now the full authorship pair rides
        `ItemResponse` and the line draws every fact the rule names.
      */}
      {isEdit && existing ? <AuthorshipLine record={existing} /> : null}

      {/* IT-S8 — files and images on the item. "Save first" on a new item;
          permission follows inventory.* */}
      <AttachmentsPanel kind="item" entityId={itemId} />
    </form>
  );
}
